import { ChevronDown, Mic, Radio, Tv } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useSeries } from '@/hooks/useSeries'
import type { SeriesType, SeriesWithCount } from '@/types/series'

interface SeriesSelectorProps {
  value: string | undefined
  onChange: (seriesId: string) => void
  disabled?: boolean
  error?: string
}

const SERIES_TYPE_ICONS: Record<SeriesType, React.ElementType> = {
  podcast: Mic,
  channel: Radio,
  show: Tv,
}

export function SeriesSelector({
  value,
  onChange,
  disabled,
  error,
}: SeriesSelectorProps) {
  const { data: seriesList, isLoading } = useSeries()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedSeries = seriesList?.find((s) => s.id === value)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (series: SeriesWithCount) => {
    onChange(series.id)
    setIsOpen(false)
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled || isLoading}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border
          bg-white dark:bg-zinc-800 text-left
          ${error
            ? 'border-rose-500 focus:ring-rose-500'
            : 'border-zinc-300 dark:border-zinc-600 focus:ring-primary-500'
          }
          focus:outline-none focus:ring-2 focus:border-transparent
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        {selectedSeries ? (
          <div className="flex items-center gap-2">
            {(() => {
              const Icon = SERIES_TYPE_ICONS[selectedSeries.series_type]
              return <Icon className="w-4 h-4 text-zinc-500" />
            })()}
            <span className="text-zinc-900 dark:text-zinc-100">
              {selectedSeries.name}
            </span>
            <span className="text-xs text-zinc-500 dark:text-zinc-400">
              ({selectedSeries.episode_count} episodes)
            </span>
          </div>
        ) : (
          <span className="text-zinc-400 dark:text-zinc-500">
            {isLoading ? 'Loading...' : 'Select a collection'}
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-zinc-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {error && (
        <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && seriesList && seriesList.length > 0 && (
        <div className="absolute z-50 w-full mt-1 py-1 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700 max-h-60 overflow-auto">
          {seriesList.map((series) => {
            const Icon = SERIES_TYPE_ICONS[series.series_type]
            const isSelected = series.id === value

            return (
              <button
                key={series.id}
                type="button"
                onClick={() => handleSelect(series)}
                className={`
                  w-full flex items-center gap-2 px-3 py-2 text-left
                  ${isSelected
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                    : 'hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100'
                  }
                `}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 truncate">{series.name}</span>
                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                  {series.episode_count} ep
                </span>
              </button>
            )
          })}
        </div>
      )}

      {/* Empty state */}
      {isOpen && (!seriesList || seriesList.length === 0) && !isLoading && (
        <div className="absolute z-50 w-full mt-1 py-3 px-4 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            No collections available. Create a collection first.
          </p>
        </div>
      )}
    </div>
  )
}
